import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FileLink,
  Pill,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { AnnouncementConfig, AnnouncementDetail, FileInfo } from 'web-library/models/Announcements';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { stringToSemanticDisplay } from '../../../utils/DateUtils';
import AnnouncementContent from './AnnouncementContent';

const useStyles = makeStyles()((theme) =>
({
  cardHeader: {
    paddingBottom: 0,
  },
  cardHeaderContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 34,
  },
  date: {
    color: theme.color.text.textTertiary,
  },
  cardContent: {
    paddingTop: 0,
    paddingBottom: '4px !important',
  },
  title: {
    margin: '8px 0',
  },
  description: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    margin: '8px 0',
    fontWeight: 400,
  },
  readMoreButton: {
    marginTop: theme.spacing(1),
    padding: 0,
    minWidth: 0,
    color: theme.color.link.linkDefault01,
    '&:hover': {
      color: theme.color.link.linkHover01,
    },
  },
  readMoreIcon: {
    flexDirection: 'row-reverse',
  },
}));

interface AnnouncementCardProps {
  announcementDetail: AnnouncementDetail;
  announcementConfig: AnnouncementConfig;
  showReadMore?: boolean;
}

const AnnouncementCard = ({
  announcementDetail,
  announcementConfig,
  showReadMore = false,
}: AnnouncementCardProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [openAnnouncementModal, setOpenAnnouncementModal] = useState<boolean>(false);

  const [announcementUuid, setAnnouncementUuid] = useState<string>('');

  const handleOpenReadMoreModal = () => {
    setAnnouncementUuid(announcementDetail.uuid || '');
    setOpenAnnouncementModal(true);
  };

  return (
    <Card>
      <CardHeader className={classes.cardHeader}>
        <Box className={classes.cardHeaderContent}>
          <Typography variant="subtitle2" className={classes.date}>
            {stringToSemanticDisplay(announcementDetail.announcementStartDate)}
          </Typography>
          {announcementConfig.priorityEnabled && announcementDetail.priority && (
            <Pill label={t('admin->>AnnouncementNoticeBoard.Priority')} variant="warning" />
          )}
        </Box>
      </CardHeader>
      <CardContent className={classes.cardContent}>
        <Box minHeight={95}>
          {announcementConfig.titleEnabled && (
            <Typography variant="h4" className={classes.description}>
              {announcementDetail.title}
            </Typography>
          )}
          {announcementConfig.descriptionEnabled && (
            <Typography variant="body2" className={classes.description}>
              {parse(announcementDetail.description || '')}
            </Typography>
          )}
        </Box>
        {showReadMore ? (
          <Button
            variant="text"
            size="small"
            className={classes.readMoreButton}
            onClick={handleOpenReadMoreModal}
          >
            <IconWithLabel
              iconName={IconName.arrowRight}
              className={classes.readMoreIcon}
              gap={4}
              typographyProps={{
                variant: 'subtitle2',
              }}
              label={t('admin->>AnnouncementNoticeBoard.ReadMore')}
            />
          </Button>
        ) : (
          announcementConfig.documentEnabled && (
            <Box marginBottom={3}>
              {announcementDetail?.files?.map((fileInfo: FileInfo) => (
                <FileLink key={fileInfo.url} fileInfo={fileInfo} />
              ))}
            </Box>
          )
        )}
        <AnnouncementContent
          openAnnouncementModal={openAnnouncementModal}
          setOpenAnnouncementModal={setOpenAnnouncementModal}
          isPriorityNotice={false}
          announcementType="ANNOUNCEMENTS"
          announcementUuid={announcementUuid}
        />
      </CardContent>
    </Card>
  );
};

export default AnnouncementCard;
